import React, { FC, useEffect, useState } from 'react'
import { change, Field, InjectedFormProps, reduxForm } from 'redux-form'
import { Button, Col, Collapse, Form, Row } from 'antd'
import { useTranslation } from 'react-i18next'
import { debounce } from 'lodash'
import { useDispatch, useSelector } from 'react-redux'

// components
import DynamicIcon from '../../DynamicIcon'
import ValidationModal from '../../modals/ValidationModal'

// atoms
import InputField from '../../../atoms/InputField'
import TypeRadioGroupField from '../../../atoms/TypeRadioGroupField'
import SelectField from '../../../atoms/SelectField'
import SwitchField from '../../../atoms/SwitchField'

// interfaces
import { IFormItemData, IIconSelectOption, IItem } from '../../../types/interfaces'

// utils
import {
	BUTTON_ACTION_OPTIONS,
	BUTTON_ACTIONS,
	BUTTON_ACTIONS_ATTRIBUTES,
	ELEMENT_ATTRIBUTES,
	ELEMENT_TYPES,
	FORM,
	ICONS,
	PROTECTION_INTERVAL_FOR_SAVING_ELEMENT,
	RADIO_BTN_GROUP_FIELD
} from '../../../utils/enums'

// reducers
import { RootState } from '../../../reducers'

// assets
import { ReactComponent as ChevronIcon } from '../../../assets/icons/BaseIcons/chevron-down-1-icon.svg'
import { ReactComponent as HeightIcon } from '../../../assets/icons/BaseMiniIcons/height-icon.svg'
import { ReactComponent as WidthIcon } from '../../../assets/icons/BaseMiniIcons/width-icon.svg'
import { ReactComponent as TrashIcon } from '../../../assets/icons/BaseIcons/trash-1-icon.svg'

// eslint-disable-next-line import/no-cycle
import validateButtonElementForm from '../validations/validateButtonElementForm'

const { Panel } = Collapse

export type ComponentProps = {
	onRemoveItem: Function
	priorities?: any[]
}
type Props = InjectedFormProps<IFormItemData, ComponentProps> &
	ComponentProps & {
		handleSubmit: Function
	}

const ButtonElementForm: FC<Props> = (props) => {
	const { t } = useTranslation()
	const dispatch: any = useDispatch()
	const { handleSubmit, onRemoveItem } = props
	const [isValidationModalShowed, setValidationModalShowed] = useState(false)
	const disable = useSelector((state: RootState) => state.formTemplate.disable)
	const buttonForm = useSelector((state: RootState) => state.form?.[FORM.BUTTON_ELEMENT_FORM]?.values)
	const validableComponents = useSelector((state: RootState) => state.formTemplate.validableComponents)
	const tabs = useSelector((state: RootState) => state.formTemplate.tabs)
	const formValues = useSelector((state: RootState) => state.form?.[FORM.BUTTON_ELEMENT_FORM]?.values)
	const formItemId = formValues?.i
	const procedures = useSelector((state: RootState) => state.formTemplate.procedures)
	const configs = useSelector((state: RootState) => state.formTemplate.configs)
	const openModal = useSelector((state: RootState) => state.form?.[FORM.BUTTON_ELEMENT_FORM]?.values?.[BUTTON_ACTIONS_ATTRIBUTES.OPEN_MODAL])
	// const [buttonActions, setButtonActions] = useState<IItem[]>([])
	//
	// // on init map button actions array
	// useEffect(() => {
	// 	setButtonActions(Object.values(BUTTON_ACTIONS).map((item) => ({ label: item, value: item })))
	// }, [])

	// clear modal size when admin change attribute open in modal to false
	useEffect(() => {
		if (!openModal) {
			// clear modal size and close icon
			dispatch(change(FORM.BUTTON_ELEMENT_FORM, BUTTON_ACTIONS_ATTRIBUTES.MODAL_SIZE, ''))
			dispatch(change(FORM.BUTTON_ELEMENT_FORM, BUTTON_ACTIONS_ATTRIBUTES.CLOSE_ICON_MODAL, ''))
		}
	}, [openModal, dispatch])

	const iconsOptions: IIconSelectOption[] = []

	// generate icons options
	const generateIconsOption = (icons: string[]) => {
		icons.forEach((icon: string) => {
			iconsOptions.push({ label: icon, value: icon, icon: <DynamicIcon type={icon} /> })
		})
	}

	generateIconsOption(ICONS)

	const modalSizes: IItem[] = [
		{ label: 'small - 25%', value: 'small' },
		{ label: 'middle - 60%', value: 'middle' },
		{ label: 'large - 90%', value: 'large' }
	]

	const renderButtonActionsAttributes = () => {
		if (buttonForm?.[ELEMENT_ATTRIBUTES.ACTION] === BUTTON_ACTIONS.CALL_NUMBER) {
			return (
				<>
					<Col span={24}>
						{' '}
						<Field
							component={SelectField}
							label={t('loc:Phone number')}
							getPopupContainer={(triggerNode: any) => triggerNode}
							name={BUTTON_ACTIONS_ATTRIBUTES.FORM_ITEM_ID}
							placeholder={t('loc:Select form item')}
							options={validableComponents?.data}
							size={'medium'}
							disabled={disable}
							clearBtn={true}
							loading={validableComponents?.isLoading}
						/>{' '}
					</Col>
					<Col span={24}>
						{' '}
						<Field
							component={InputField}
							label={t('loc:Priority')}
							name={'priority'}
							type={'number'}
							maxLength={3}
							placeholder={t('loc:Choose a priority')}
							size={'medium'}
							disabled={disable}
							clearBtn={true}
							loading={validableComponents?.isLoading}
						/>{' '}
					</Col>
				</>
			)
		}
		if (buttonForm?.[ELEMENT_ATTRIBUTES.ACTION] === BUTTON_ACTIONS.SEND_SMS) {
			return (
				<>
					<Col span={24}>
						{' '}
						<Field
							component={SelectField}
							label={t('loc:Phone number')}
							getPopupContainer={(triggerNode: any) => triggerNode}
							name={BUTTON_ACTIONS_ATTRIBUTES.FORM_ITEM_ID}
							placeholder={t('loc:Select form item')}
							options={validableComponents?.data}
							size={'medium'}
							disabled={disable}
							clearBtn={true}
							loading={validableComponents?.isLoading}
						/>{' '}
					</Col>
					<Col span={24}>
						{' '}
						<Field
							component={SelectField}
							label={t('loc:Body')}
							getPopupContainer={(triggerNode: any) => triggerNode}
							name={BUTTON_ACTIONS_ATTRIBUTES.SMS_BODY}
							placeholder={t('loc:Select form item')}
							options={validableComponents?.data}
							size={'medium'}
							disabled={disable}
							clearBtn={true}
							loading={validableComponents?.isLoading}
						/>{' '}
					</Col>
				</>
			)
		}
		if (buttonForm?.[ELEMENT_ATTRIBUTES.ACTION] === BUTTON_ACTIONS.SEND_EMAIL) {
			return (
				<>
					<Col span={24}>
						{' '}
						<Field
							component={SelectField}
							label={t('loc:Email')}
							getPopupContainer={(triggerNode: any) => triggerNode}
							name={BUTTON_ACTIONS_ATTRIBUTES.FORM_ITEM_ID}
							placeholder={t('loc:Select form item')}
							options={validableComponents?.data}
							size={'medium'}
							disabled={disable}
							clearBtn={true}
							loading={validableComponents?.isLoading}
						/>{' '}
					</Col>
					<Col span={24}>
						{' '}
						<Field
							component={SelectField}
							label={t('loc:Subject')}
							getPopupContainer={(triggerNode: any) => triggerNode}
							name={BUTTON_ACTIONS_ATTRIBUTES.EMAIL_SUBJECT}
							placeholder={t('loc:Select form item')}
							options={validableComponents?.data}
							size={'medium'}
							disabled={disable}
							clearBtn={true}
							loading={validableComponents?.isLoading}
						/>{' '}
					</Col>
					<Col span={24}>
						{' '}
						<Field
							component={SelectField}
							label={t('loc:Body')}
							getPopupContainer={(triggerNode: any) => triggerNode}
							name={BUTTON_ACTIONS_ATTRIBUTES.EMAIL_BODY}
							placeholder={t('loc:Select form item')}
							options={validableComponents?.data}
							size={'medium'}
							disabled={disable}
							clearBtn={true}
							loading={validableComponents?.isLoading}
						/>{' '}
					</Col>
					<Col span={24}>
						{' '}
						<Field
							component={SelectField}
							label={t('loc:Config')}
							getPopupContainer={(triggerNode: any) => triggerNode}
							name={BUTTON_ACTIONS_ATTRIBUTES.EMAIL_CONFIG}
							placeholder={t('loc:Select config')}
							options={configs.data}
							size={'medium'}
							disabled={disable}
							clearBtn={true}
							loading={configs?.isLoading}
						/>{' '}
					</Col>
				</>
			)
		}
		if (buttonForm?.[ELEMENT_ATTRIBUTES.ACTION] === BUTTON_ACTIONS.CALL_URL) {
			return (
				<>
					<Row justify={'center'} className={'mt-2 w-full'}>
						<Col span={16}>
							<p className={'leading-6'}>{t('loc:Dynamic url')}</p>
						</Col>
						<Col span={4} />
						<Col span={4}>
							<Field component={SwitchField} name={BUTTON_ACTIONS_ATTRIBUTES.URL_TYPE} className={'float-right'} disabled={disable} />
						</Col>
					</Row>
					<Col span={24}>
						{buttonForm?.[BUTTON_ACTIONS_ATTRIBUTES.URL_TYPE] ? (
							<Field
								component={SelectField}
								label={t('loc:Url')}
								getPopupContainer={(triggerNode: any) => triggerNode}
								name={BUTTON_ACTIONS_ATTRIBUTES.URL}
								options={validableComponents?.data}
								size={'medium'}
								disabled={disable}
								clearBtn={true}
								loading={validableComponents?.isLoading}
							/>
						) : (
							<Field component={InputField} label={t('loc:Url')} name={BUTTON_ACTIONS_ATTRIBUTES.URL} size={'medium'} disabled={disable} />
						)}
					</Col>
					<Col span={24}>
						<Field
							component={SelectField}
							label={t('loc:Query fields')}
							getPopupContainer={(triggerNode: any) => triggerNode}
							mode={'multiple'}
							name={BUTTON_ACTIONS_ATTRIBUTES.QUERY_ATTRIBUTES}
							placeholder={t('loc:Select field')}
							options={validableComponents?.data}
							size={'medium'}
							disabled={disable}
							maxOptions={30}
							clearBtn={true}
						/>
					</Col>
					{buttonForm?.[BUTTON_ACTIONS_ATTRIBUTES.OPEN_NEW_TAB] && (
						<Row justify={'center'} className={'mt-2 w-full'}>
							<Col span={16}>
								<p className={'leading-6'}>{t('loc:Open in modal')}</p>
							</Col>
							<Col span={4} />
							<Col span={4}>
								<Field component={SwitchField} name={BUTTON_ACTIONS_ATTRIBUTES.OPEN_MODAL} className={'float-right'} disabled={disable} />
							</Col>
						</Row>
					)}
					{buttonForm?.[BUTTON_ACTIONS_ATTRIBUTES.OPEN_MODAL] ? (
						<Row justify={'center'} className={'mt-2 w-full'}>
							<Col span={16}>
								<p className={'leading-6'}>{t('loc:Close icon')}</p>
							</Col>
							<Col span={4} />
							<Col span={4}>
								<Field component={SwitchField} name={BUTTON_ACTIONS_ATTRIBUTES.CLOSE_ICON_MODAL} className={'float-right'} disabled={disable} />
							</Col>
							<Col span={24}>
								{' '}
								<Field
									component={SelectField}
									label={t('loc:Fields')}
									getPopupContainer={(triggerNode: any) => triggerNode}
									options={modalSizes}
									name={BUTTON_ACTIONS_ATTRIBUTES.MODAL_SIZE}
									placeholder={t('loc:Modal size')}
									size={'medium'}
									disabled={disable}
									maxOptions={30}
									clearBtn={true}
								/>
							</Col>
						</Row>
					) : (
						<Row justify={'center'} className={'mt-2 w-full'}>
							<Col span={16}>
								<p className={'leading-6'}>{t('loc:Open in new tab')}</p>
							</Col>
							<Col span={4} />
							<Col span={4}>
								<Field component={SwitchField} name={BUTTON_ACTIONS_ATTRIBUTES.OPEN_NEW_TAB} className={'float-right'} disabled={disable} />
							</Col>
						</Row>
					)}
				</>
			)
		}
		if (buttonForm?.[ELEMENT_ATTRIBUTES.ACTION] === BUTTON_ACTIONS.CALL_PROCEDURE) {
			return (
				<>
					<Col span={24}>
						{' '}
						<Field
							component={SelectField}
							label={t('loc:Store procedure')}
							getPopupContainer={(triggerNode: any) => triggerNode}
							mode={'multiple'}
							options={procedures.data}
							loading={procedures.isLoading}
							name={BUTTON_ACTIONS_ATTRIBUTES.PROCEDURE}
							placeholder={t('loc:Select procedure')}
							size={'medium'}
							disabled={disable}
							clearBtn={true}
						/>{' '}
					</Col>
					<Col span={24}>
						{' '}
						<Field
							component={SelectField}
							label={t('loc:Fields')}
							getPopupContainer={(triggerNode: any) => triggerNode}
							options={validableComponents?.data}
							mode={'multiple'}
							name={BUTTON_ACTIONS_ATTRIBUTES.FIELDS_LIST}
							placeholder={t('loc:Select field')}
							size={'medium'}
							disabled={disable}
							clearBtn={true}
						/>{' '}
					</Col>
				</>
			)
		}
		if (buttonForm?.[ELEMENT_ATTRIBUTES.ACTION] === BUTTON_ACTIONS.GO_TO_NEXT_TAB) {
			return (
				<Col span={24}>
					{' '}
					<Field
						component={SelectField}
						label={t('loc:Tab key')}
						getPopupContainer={(triggerNode: any) => triggerNode}
						options={tabs.data}
						loading={tabs.isLoading}
						name={BUTTON_ACTIONS_ATTRIBUTES.TAB}
						placeholder={t('loc:Select tab key')}
						size={'medium'}
						disabled={disable}
						clearBtn={true}
					/>{' '}
				</Col>
			)
		}
		return undefined
	}

	return (
		<>
			<Form layout={'vertical'} onSubmitCapture={handleSubmit}>
				<Row className={'mx-4 mb-4'} justify={'space-between'}>
					<h3>{t('loc:Button')}</h3>
					<TrashIcon className={'trashIcon'} onClick={() => onRemoveItem(formItemId)} />
				</Row>
				<Row className={'ml-4 mr-4 mb-4'} justify={'center'}>
					<Col span={24}>
						<Field
							component={SelectField}
							label={t('loc:Action')}
							getPopupContainer={(triggerNode: any) => triggerNode}
							name={ELEMENT_ATTRIBUTES.ACTION}
							options={BUTTON_ACTION_OPTIONS()}
							size={'medium'}
							disabled={disable}
							clearBtn={true}
						/>
					</Col>
					{renderButtonActionsAttributes()}
				</Row>
				<Collapse expandIcon={({ isActive }) => <ChevronIcon rotate={isActive ? 0 : 180} />} expandIconPosition={'end'} ghost>
					<Panel header={t('loc:Size')} key='1'>
						<Row justify={'center'} className={'mt-2 w-full'}>
							<Col span={11}>
								<Field
									className={'element-width'}
									component={InputField}
									name={ELEMENT_ATTRIBUTES.COL}
									size={'medium'}
									suffix={<WidthIcon />}
									disabled={disable}
									maxLength={2}
								/>
							</Col>
							<Col span={2} />
							<Col span={11}>
								<Field
									className={'element-height'}
									component={InputField}
									name={ELEMENT_ATTRIBUTES.ROW}
									size={'medium'}
									suffix={<HeightIcon />}
									disabled={disable}
									maxLength={3}
								/>
							</Col>
						</Row>
						<Row justify={'center'} className={'my-2 w-full'}>
							<Col span={11} className={'h-34px flex flex-col justify-center'}>
								<p className={'m-0'}>{t('loc:Button size')}</p>
							</Col>
							<Col span={2} />
							<Col span={11} className={'h-34px'}>
								<Field
									component={TypeRadioGroupField}
									optionsType={RADIO_BTN_GROUP_FIELD.TWO_SIZE}
									name={ELEMENT_ATTRIBUTES.SIZE}
									size={'medium'}
									disabled={disable}
								/>
							</Col>
						</Row>
					</Panel>
					<Panel header={t('loc:Label')} key='2'>
						<Field component={InputField} placeholder={t('loc:Enter label')} name={ELEMENT_ATTRIBUTES.LABEL} size={'medium'} disabled={disable} />
						<Row justify={'center'} className={'mt-2 w-full'}>
							<Col className={'h-34px flex flex-col justify-center'} span={11}>
								<p className={'m-0'}>{t('loc:Style')}</p>
							</Col>
							<Col span={2} />
							<Col span={11} className={'h-34px'}>
								<Field
									component={TypeRadioGroupField}
									optionsType={RADIO_BTN_GROUP_FIELD.TEXT_TYPE}
									name={ELEMENT_ATTRIBUTES.LABEL_TEXT_TYPE}
									disabled={disable}
								/>
							</Col>
						</Row>
						<Row justify={'center'} className={'my-2 w-full'}>
							<Col span={24}>
								<Field
									component={SelectField}
									placeholder={t('loc:Select prefix icon')}
									getPopupContainer={(triggerNode: any) => triggerNode}
									name={ELEMENT_ATTRIBUTES.PREFIX_ICON}
									options={iconsOptions}
									size={'medium'}
									clearBtn={true}
									disabled={disable}
									showSearch
								/>
							</Col>
						</Row>
						<Row justify={'center'} className={'my-2 w-full'}>
							<Col span={24}>
								<Field
									component={SelectField}
									placeholder={t('loc:Select suffix icon')}
									getPopupContainer={(triggerNode: any) => triggerNode}
									name={ELEMENT_ATTRIBUTES.SUFFIX_ICON}
									options={iconsOptions}
									size={'medium'}
									clearBtn={true}
									disabled={disable}
									showSearch
								/>
							</Col>
						</Row>
					</Panel>
					<Panel header={t('loc:Validations')} key='3'>
						<Button
							className={'unfilled-btn w-full h-25px mb-2'}
							disabled={disable}
							onClick={() => {
								setValidationModalShowed(true)
							}}
						>
							{t('loc:Validation options')}
						</Button>
					</Panel>
				</Collapse>
			</Form>
			<ValidationModal
				width={460}
				typeOfElementForm={FORM.BUTTON_ELEMENT_FORM}
				isVisible={isValidationModalShowed}
				onCancel={() => {
					setValidationModalShowed(false)
				}}
				elementLabel={formValues?.label}
				elementType={ELEMENT_TYPES.BUTTON}
			/>
		</>
	)
}

const form = reduxForm<IFormItemData, ComponentProps>({
	form: FORM.BUTTON_ELEMENT_FORM,
	forceUnregisterOnUnmount: true,
	touchOnChange: true,
	destroyOnUnmount: true,
	validate: validateButtonElementForm,
	onChange: debounce((values, dispatch, props) => {
		if (props.anyTouched) {
			props.submit()
		}
	}, PROTECTION_INTERVAL_FOR_SAVING_ELEMENT)
})(ButtonElementForm)

export default form
